import React from "react";
import { Clause, Register, RegisterRow, Terms, Party } from "./ContractDocumentParts";
import ContractMasthead from "./ContractMasthead";
import { ContractOptionRow, ContractVehicleSourceNote } from "./ContractReviewUi";
import { VerifiedTick } from "./StatusMark";
import { formatSekAmount } from "../../lib/format.jsx";

const formatSEK = (value) => (value ? formatSekAmount(value) : "-");

// Shared by both print entry points (ContractAgreementFlow's own "Print"
// button and ContractPreviewModal's "Download PDF"): react-to-print's
// default page style only zeroes the @page margin, it doesn't know this
// document has its own on-screen measure/border — left as is, that reads as
// a narrow column with wide dead margins on A4 rather than a filled page.
export const CONTRACT_PRINT_PAGE_STYLE = `
  @page { size: A4; margin: 12mm; }
  @media print {
    .ds-doc { max-width: none !important; width: 100% !important; border: none !important; border-radius: 0 !important; }
  }
`;

// The read-only purchase agreement document itself — the nine numbered
// clauses, ending on the signature clause's seller/buyer rows — shared
// between the standalone /Contract/:id page (ContractAgreementFlow) and the
// transaction record's "View the contract" preview modal (ContractPreviewModal),
// so both ever show the exact same contract, never two different renderings
// of it. Presentation only: the caller owns data fetching, print/download and
// any surrounding chrome.
//
// Clause wording/structure mirrors BuyerReviewSections.jsx (the live
// buyer/seller review — the one already verified against the mock-up) so the
// retained document reads as the same contract, not a stale, differently
// worded copy of it. The difference is presentation only: this is the
// retained document, not a live editing/signing surface, so every
// certification here renders in its FINAL confirmed state as plain text —
// no buttons, no editable fields, no interactive options. Signed state and
// the debt/loan choice come from the contract's own persisted fields, never
// from a live SignalR session (there is none once the deal is closed).
//
// `viewerRole` ("seller" | "buyer") renders the contract as THAT party's own copy, the way the
// party saw it when signing: the seller's copy carries the seller's odometer certification and
// the seller's wording and choices for debts and loans; the buyer's copy carries the buyer's
// inspection confirmation, the seller's certification as a note on the odometer row, and the
// buyer's wording. Left out, the neutral document (both confirmations) is rendered, as before.
export default function ContractDocumentView({ t, contractData, buyerName, viewerRole = null, hideSourceNote = false, signaturesFooterKey = "agreement.signaturesFooter", signaturesFooterBold = false }) {
  const asSeller = viewerRole === "seller";
  const asBuyer = viewerRole === "buyer";
  const showSellerCertification = !asBuyer;
  const showBuyerConfirmation = !asSeller;
  const sellerProfile = viewerRole && contractData?.sellerBankIdSSN ? { bankIdSSN: contractData.sellerBankIdSSN } : null;
  const buyerProfile = viewerRole && contractData?.buyerBankIdSSN ? { bankIdSSN: contractData.buyerBankIdSSN } : null;
  const certifiedByNote = (
    <span className="ds-doc-attest-line">
      <VerifiedTick width={12} height={10} />
      <span>{t("contractUi.certifiedBy").replace("{name}", contractData?.sellerUserFullName || t("role.seller"))}</span>
    </span>
  );
  // Debts and loans: the seller chose between "none" and "seller"; the buyer's copy lists all three.
  const debtLabel = asSeller ? t("seller.debts.title") : t("buyer.debtsTitle");
  const debtOptions = asSeller
    ? ["none", "seller"].map((opt) => ({ value: opt, label: t(`seller.debts.options.${opt}`) }))
    : ["none", "seller", "buyer"].map((option) => ({ value: option, label: t(`buyer.debts${option[0].toUpperCase()}${option.slice(1)}`) }));
  const loanOptions = asSeller
    ? ["none", "seller"].map((opt) => ({ value: opt, label: t(`seller.loan.options.${opt}`) }))
    : ["none", "seller", "buyer"].map((option) => ({ value: option, label: t(`buyer.loan${option[0].toUpperCase()}${option.slice(1)}`) }));

  // Every column on tbl_CarInfoMain, styled per the mock-up's register-row
  // layout, plus the mock-up-only concepts we have no column for (vin, color,
  // engine, number of keys) — shown anyway, always empty, since the mock-up
  // always shows them. Nothing hidden, nothing renamed away.
  const hasFourWheelDrive = contractData?.carInfoFourWheelDrive != null && contractData.carInfoFourWheelDrive !== "";
  const fourWheelDrive = String(contractData?.carInfoFourWheelDrive).toLowerCase() === "true";

  const vehicleFacts = contractData
    ? [
        ["vin", null],
        ["color", null],
        ["registrationNo", contractData.carInfoRegistrationNo],
        ["vehicleDesignation", contractData.carInfoVehicleDesignation],
        ["tradeName", contractData.carInfoTradeName],
        // "Year model" is the primary model-year field; Bilvision's Årsmodell
        // (YearModel) is often blank, so fall back to Fordonsår (VehicleYears)
        // rather than showing both/neither.
        ["yearModel", contractData.carInfoYearModel || contractData.carInfoVehicleYears],
        ["engine", null],
        ["fuel", contractData.carInfoFuel],
        ["transmission", contractData.carInfoWaxelbarge],
        ["drivetrain", hasFourWheelDrive ? t(fourWheelDrive ? "buyer.fourWheelDriveYes" : "buyer.fourWheelDriveNo") : null],
        ["electricVehicleConfiguration", contractData.carInfoElectricVehicleConfiguration],
        ["firstInTraffic", contractData.carInfoRegistrationDate],
        ["previousOwners", contractData.carInfoNumberOfUsers],
        ["inspectionLast", contractData.carInfoInspectionDate],
        ["inspectionNext", contractData.carInfoInspectionDateEMPTY],
        ["previousInspectionDate", contractData.carInfoPreviousInspectionDate],
        ["dispensationInspectionGroup", contractData.carInfoDispensationInspectionGroup],
        ["inspectionGroup", contractData.carInfoInspectionGroup],
        ["inspectionStation", contractData.carInfoInspectionStation],
        ["inspectionProgramCode", contractData.carInfoInspectionProgramCode],
        ["previousInspectionProgramCode", contractData.carInfoPreviousInspectionProgramCode],
        // the seller's stated reading / keys as saved on the contract (the registry value stands until stated)
        ["odometer", contractData.odometerKm ?? contractData.carInfoFeedingStall],
        ["keysCount", contractData.keysCount],
        ["tax", contractData.carInfoTax],
        ["malus", contractData.carInfoMalus],
      ]
    : [];

  const debtOption = contractData?.debtOption ?? "none";
  const loanOption = contractData?.loanLienOption ?? "none";

  return (
    <article className="ds-doc ds-doc--terms">
      <ContractMasthead
        t={t}
        regNo={contractData?.carInfoRegistrationNo}
        vehicleDesignation={contractData?.carInfoVehicleDesignation}
        vehicleYears={contractData?.carInfoVehicleYears}
        vehicleType={contractData?.vahicleTypeOptionName}
        sellerName={contractData?.sellerUserFullName}
        buyerName={buyerName}
        lastChangedAt={contractData?.lastModificationTime || contractData?.creationTime}
      />

      <Clause id="section-vehicle" number={1} title={t("buyer.vehicleInformation")}>
        <Register>
          {vehicleFacts.map(([key, val]) => (
            <RegisterRow key={key} label={t(`buyer.${key}`)} note={asBuyer && key === "odometer" ? certifiedByNote : undefined}>
              {val || <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>}
            </RegisterRow>
          ))}
          <RegisterRow label={t("buyer.equipmentIncluded")} wide>
            <span className="ds-doc-value">
              {String(contractData?.equipmentIncluded ?? t("agreement.equipmentStandardText")).split("\n").map((line) => line.trim()).filter(Boolean)
                .map((line, index) => <span key={`${line}:${index}`} className="block">{line}</span>)}
            </span>
          </RegisterRow>
        </Register>

        {/* The seller's odometer certification, in its final confirmed
            state — no button, this is the retained record of it. */}
        {showSellerCertification && (
          <div className="ds-doc-attest" data-attested="true">
            <p className="ds-doc-prose ds-doc-attest-statement">{t("contractUi.odometerStatement")}</p>
            <div className="ds-doc-attest-record">
              <p className="ds-doc-attest-line" role="status">
                <VerifiedTick width={12} height={10} />
                <span>{t("contractUi.certifiedBy").replace("{name}", contractData?.sellerUserFullName || t("role.seller"))}</span>
              </p>
            </div>
          </div>
        )}
        {!hideSourceNote && <ContractVehicleSourceNote t={t} />}
      </Clause>

      <Clause id="section-parties" number={2} title={t("agreement.partiesTitle")}>
        <div className="ds-doc-parties">
          <Party t={t} roleKey="seller" name={contractData?.sellerUserFullName} profile={sellerProfile} />
          <Party t={t} roleKey="buyer" name={buyerName} profile={buyerProfile} />
        </div>
      </Clause>

      <Clause id="section-price" number={3} title={t("contractUi.purchasePriceTitle")}>
        <div className="ds-doc-price-line">
          <p className="ds-doc-price">{formatSEK(contractData?.carValuationBySeller)}</p>
        </div>
      </Clause>

      <Clause id="section-delivery" number={4} title={t("contractUi.deliveryTitle")}>
        <Terms clause={4} items={[t("contractUi.deliveryRiskText")]} />
      </Clause>

      <Clause id="section-condition" number={5} title={t("seller.conditionDefects.title")}>
        <Terms clause={5} items={[t("contractUi.conditionAsIs"), t("contractUi.conditionClaim"), t("contractUi.conditionNotify")]} />
        <Register className="mt-4">
          <RegisterRow label={t("buyer.knownDefects")} wide>
            {String(contractData?.knownDefects || "").trim() ? (
              <span className="ds-doc-value">
                {String(contractData.knownDefects).split("\n").map((line) => line.trim()).filter(Boolean)
                  .map((line, index) => <span key={`${line}:${index}`} className="block">{line}</span>)}
              </span>
            ) : <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>}
          </RegisterRow>
        </Register>

        {/* The buyer's confirmation that they reviewed the seller's known
            defects, in its final confirmed state — no button. */}
        {showBuyerConfirmation && (
          <div className="ds-doc-attest" data-attested="true">
            <p className="ds-doc-prose ds-doc-attest-statement">{t("contractUi.buyerInspectionStatement")}</p>
            <div className="ds-doc-attest-record">
              <p className="ds-doc-attest-line" role="status">
                <VerifiedTick width={12} height={10} />
                <span>{t("contractUi.buyerConfirmed")}</span>
              </p>
            </div>
          </div>
        )}
      </Clause>

      <Clause id="section-debt" number={6} title={t("contractUi.debtsLoansTitle")}>
        <Register>
          <ContractOptionRow
            t={t}
            label={debtLabel}
            value={debtOption}
            options={debtOptions}
          />
          <ContractOptionRow
            t={t}
            label={t("contractUi.loanTitle")}
            value={loanOption}
            options={loanOptions}
          />
        </Register>
        <p className="ds-doc-source">{t("contractUi.debtsLoansSource")}</p>
      </Clause>

      <Clause id="section-ownership" number={7} title={t("contractUi.ownershipTitle")}>
        <Terms clause={7} items={[t("contractUi.ownershipRetention"), t("contractUi.ownershipDocuments"), t("contractUi.ownershipDrivingBan")]} />
      </Clause>

      <Clause id="section-transport" number={8} title={t("contractUi.transportTitle")}>
        <Terms clause={8} items={[t("contractUi.transportCommitment"), t("contractUi.transportNote")]} />
      </Clause>

      {/* Stacked rows (.ds-contract-signature-list), not the clause 2 identity
          grid — one party per row, divided by hairlines, matching the
          mockup's own signature clause (ContractSignatureBlock's
          SignatureLine list). Signed state comes from the server-recorded BankID
          signing times (sellerSignedAt / buyerSignedAt) — this is the retained,
          historical document, not the live in-session signing surface. */}
      <Clause id="section-signature" number={9} title={t("agreement.signaturesTitle")}>
        <ol className="ds-contract-signature-list" aria-label={t("agreement.signaturesTitle")}>
          {[
            // Signed = the server recorded a verified BankID signature (signedAt is backend-owned).
            // sellerDealComplete also means "seller confirmed the buyer", so it is not proof.
            { roleKey: "seller", name: contractData?.sellerUserFullName, confirmed: !!contractData?.sellerSignedAt },
            { roleKey: "buyer", name: buyerName, confirmed: !!contractData?.buyerSignedAt },
          ].map(({ roleKey, name, confirmed }) => (
            <li key={roleKey}>
              <div>
                <p className="ds-doc-party-name">{name || "-"}</p>
                <p className="ds-doc-party-role">{t(`role.${roleKey}`)}</p>
              </div>
              <p className="ds-contract-signature-status">
                {confirmed && <VerifiedTick width={12} height={10} />}
                {t(confirmed ? "agreement.signedWith" : "agreement.notYetSigned")}
              </p>
            </li>
          ))}
        </ol>
        <p className={`ds-doc-row-note mt-3${signaturesFooterBold ? " font-semibold text-ds-ink" : ""}`}>{t(signaturesFooterKey)}</p>
      </Clause>
    </article>
  );
}
